import type {
  ActionConsumerAtomicOutcome,
  ActionConsumerContract,
  ActionConsumerOutcome,
  ConsumerValueSchema,
  RuntimeActionHandler,
} from "./action-consumer-contract"

export interface RegisteredActionConsumer {
  contract: ActionConsumerContract
  handler: RuntimeActionHandler
}

export class ActionConsumerCatalog {
  private readonly canonical = new Map<string, RegisteredActionConsumer>()
  private readonly aliases = new Map<string, string>()

  registerBuiltin(
    contract: ActionConsumerContract,
    handler: RuntimeActionHandler,
  ): void {
    validateContract(contract)
    if (typeof handler !== "function") {
      throw new Error(`Action consumer contract "${contract.action}" requires a handler`)
    }
    if (this.canonical.has(contract.action)) {
      throw new Error(`Action consumer duplicate canonical action: ${contract.action}`)
    }
    if (this.aliases.has(contract.action)) {
      throw new Error(`Action consumer alias cycle: canonical "${contract.action}" is already an alias`)
    }
    const pendingAliases = new Set<string>()
    for (const alias of contract.runtimeAliases) {
      if (alias === contract.action) {
        throw new Error(`Action consumer alias cycle: ${contract.action} -> ${alias}`)
      }
      if (pendingAliases.has(alias) || this.canonical.has(alias) || this.aliases.has(alias)) {
        throw new Error(`Action consumer alias collision: ${alias}`)
      }
      pendingAliases.add(alias)
    }

    const frozen = freezeContract(contract)
    this.canonical.set(frozen.action, { contract: frozen, handler })
    for (const alias of frozen.runtimeAliases) this.aliases.set(alias, frozen.action)
  }

  resolve(action: string): RegisteredActionConsumer | undefined {
    return this.canonical.get(this.aliases.get(action) ?? action)
  }

  listActionConsumerContracts(): readonly ActionConsumerContract[] {
    return Object.freeze(Array.from(this.canonical.values(), ({ contract }) => contract))
  }
}

function validateContract(contract: ActionConsumerContract): void {
  if (!contract || typeof contract !== "object") {
    throw new Error("Builtin registration requires an action consumer contract")
  }
  if (contract.schemaVersion !== "cogo.action-consumer.v1") {
    throw new Error(`Unsupported action consumer schema version: ${String(contract.schemaVersion)}`)
  }
  if (typeof contract.action !== "string" || !contract.action.trim()) {
    throw new Error("Action consumer contract requires a canonical action")
  }
  if (!Array.isArray(contract.runtimeAliases) || !Array.isArray(contract.reads)) {
    throw new Error(`Action consumer contract "${contract.action}" has invalid arrays`)
  }
  const readNames = new Set<string>()
  for (const read of contract.reads) {
    if (!read.path) throw new Error(`Action consumer contract "${contract.action}" has an empty read path`)
    readNames.add(read.path)
    for (const alias of read.aliases ?? []) readNames.add(alias)
  }
  const validateAtomicOutcome = (outcome: ActionConsumerAtomicOutcome): void => {
    if (outcome.kind === "state-target") {
      const hasTargetParam = Object.prototype.hasOwnProperty.call(outcome, "targetParam")
      const hasDefaultTarget = Object.prototype.hasOwnProperty.call(outcome, "defaultTarget")
      const hasDerivedTarget = Object.prototype.hasOwnProperty.call(
        outcome,
        "defaultTargetFromParam",
      )
      if (!hasTargetParam && !hasDefaultTarget && !hasDerivedTarget) {
        throw new Error("Action consumer state target requires at least one target source")
      }
      if (
        hasTargetParam &&
        (typeof outcome.targetParam !== "string" || !outcome.targetParam)
      ) {
        throw new Error("Action consumer state target targetParam must be a non-empty string")
      }
      if (
        hasDefaultTarget &&
        (typeof outcome.defaultTarget !== "string" || !outcome.defaultTarget)
      ) {
        throw new Error("Action consumer state target defaultTarget must be a non-empty string")
      }
      if (outcome.targetParam && !readNames.has(outcome.targetParam)) {
        throw new Error(
          `Action consumer outcome targetParam must be a declared read: ${outcome.targetParam}`,
        )
      }
      if (hasDefaultTarget && hasDerivedTarget) {
        throw new Error("Action consumer state target cannot declare both defaultTarget and defaultTargetFromParam")
      }
      if (hasDerivedTarget) {
        const derivedTarget = outcome.defaultTargetFromParam
        if (!derivedTarget || typeof derivedTarget !== "object") {
          throw new Error("Action consumer defaultTargetFromParam requires an object")
        }
        if (!derivedTarget.prefix) {
          throw new Error("Action consumer defaultTargetFromParam requires a prefix")
        }
        if (
          derivedTarget.fallback !== undefined &&
          !derivedTarget.fallback
        ) {
          throw new Error("Action consumer defaultTargetFromParam fallback must be non-empty")
        }
        if (!readNames.has(derivedTarget.param)) {
          throw new Error(
            `Action consumer defaultTargetFromParam param must be a declared read: ${derivedTarget.param}`,
          )
        }
      }
    }
    if (outcome.kind === "host-registry") {
      if (!readNames.has(outcome.selectorParam)) {
        throw new Error(
          `Action consumer outcome selectorParam must be a declared read: ${outcome.selectorParam}`,
        )
      }
      if (outcome.payloadParam && !readNames.has(outcome.payloadParam)) {
        throw new Error(
          `Action consumer outcome payloadParam must be a declared read: ${outcome.payloadParam}`,
        )
      }
    }
  }
  if (contract.outcome.kind === "effect-set") {
    if (contract.outcome.branches.length === 0) {
      throw new Error(`Action consumer contract "${contract.action}" has an empty effect-set`)
    }
    for (const branch of contract.outcome.branches) {
      if (branch.when && !readNames.has(branch.when.param)) {
        throw new Error(
          `Action consumer outcome branch param must be a declared read: ${branch.when.param}`,
        )
      }
      if (branch.when) {
        const hasEquals = Array.isArray(branch.when.equals)
        const hasPresent = typeof branch.when.present === "boolean"
        if (hasEquals === hasPresent || (hasEquals && branch.when.equals!.length === 0)) {
          throw new Error(
            `Action consumer outcome branch must declare exactly one non-empty equals or present condition: ${branch.when.param}`,
          )
        }
      }
      if (branch.effects.length === 0) {
        throw new Error(`Action consumer contract "${contract.action}" has an empty effect branch`)
      }
      for (const outcome of branch.effects) validateAtomicOutcome(outcome)
    }
  } else if (contract.outcome.kind !== "none") {
    validateAtomicOutcome(contract.outcome)
  }
}

function freezeContract(contract: ActionConsumerContract): ActionConsumerContract {
  const reads = contract.reads.map((read) => ({
    ...read,
    ...(read.aliases ? { aliases: [...read.aliases] } : {}),
  }))
  const clone: ActionConsumerContract = {
    ...contract,
    runtimeAliases: [...contract.runtimeAliases],
    reads,
    outcome: cloneOutcome(contract.outcome),
    requiredHostCapabilities: [...contract.requiredHostCapabilities],
  }
  return deepFreeze(clone)
}

function cloneOutcome(outcome: ActionConsumerOutcome): ActionConsumerOutcome {
  if (outcome.kind === "effect-set") {
    return {
      kind: "effect-set",
      branches: outcome.branches.map((branch) => ({
        ...(branch.when
          ? {
              when: {
                param: branch.when.param,
                ...(branch.when.equals ? { equals: [...branch.when.equals] } : {}),
                ...(branch.when.present !== undefined ? { present: branch.when.present } : {}),
              },
            }
          : {}),
        effects: branch.effects.map((effect) => cloneAtomicOutcome(effect)),
      })),
    }
  }
  if (outcome.kind === "none") return { kind: "none" }
  return cloneAtomicOutcome(outcome)
}

function cloneAtomicOutcome(outcome: ActionConsumerAtomicOutcome): ActionConsumerAtomicOutcome {
  if (outcome.kind === "state-target") {
    return {
      ...outcome,
      ...(outcome.defaultTargetFromParam
        ? { defaultTargetFromParam: { ...outcome.defaultTargetFromParam } }
        : {}),
      ...(outcome.targetSuffixes ? { targetSuffixes: [...outcome.targetSuffixes] } : {}),
      ...(outcome.envelope ? { envelope: cloneValueSchema(outcome.envelope) } : {}),
    }
  }
  if (outcome.kind === "return-capture") {
    return {
      ...outcome,
      ...(outcome.envelope ? { envelope: cloneValueSchema(outcome.envelope) } : {}),
    }
  }
  return { ...outcome }
}

function cloneValueSchema(schema: ConsumerValueSchema): ConsumerValueSchema {
  if (schema.kind === "array") {
    return { kind: "array", items: cloneValueSchema(schema.items) }
  }
  if (schema.kind === "object") {
    return {
      kind: "object",
      properties: Object.fromEntries(
        Object.entries(schema.properties).map(([key, value]) => [key, cloneValueSchema(value)]),
      ),
      required: [...schema.required],
    }
  }
  return { ...schema }
}

function deepFreeze<T>(value: T): T {
  if (value === null || typeof value !== "object" || Object.isFrozen(value)) return value
  for (const nested of Reflect.ownKeys(value as object)) {
    deepFreeze((value as Record<PropertyKey, unknown>)[nested])
  }
  return Object.freeze(value)
}
